
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './components/Filters/CertificatesFilter.css';
import { getApiV1Url } from './config/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import {
    Letter,
    Link,
    ApiResponse,
    LettersFilterValues,
    LettersGraphProps
} from './types/letterGraph';
import {
    getColor,
    getEdgeColor,
    getChainNodes,
    getRootNodes,
    getLettersWithoutResponse
} from './utils/letterGraphUtils';

import LettersFilter from './components/LetterGraph/LettersFilter';
import LetterInfoPanel from './components/LetterGraph/LetterInfoPanel';
import TreeModal from './components/LetterGraph/TreeModal';
import GraphControls from './components/LetterGraph/GraphControls';

declare global {
    interface Window {
        vis: any;
    }
}

// Возвращает дату в формате YYYY-MM-DD на 180 дней раньше текущей
const getDefaultDateFrom = (): string => {
    const date = new Date();
    date.setDate(date.getDate() - 180);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const LetterGraphFlowPage: React.FC<LettersGraphProps> = ({ isFilterOpen, setIsFilterOpen }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [filterType, setFilterType] = useState<'all' | 'Исходящее' | 'Входящее'>('all');
    const [connectedOnly, setConnectedOnly] = useState<boolean>(false);
    const [globalSearch, setGlobalSearch] = useState<string>('');

    const [metaMs, setMetaMs] = useState<number | undefined>(undefined);
    const [counts, setCounts] = useState<{ nodes: number; edges: number }>({ nodes: 0, edges: 0 });
    const [graphReady, setGraphReady] = useState(false);

    const [allLetters, setAllLetters] = useState<Letter[]>([]);
    const [allLinks, setAllLinks] = useState<Link[]>([]);

    const [selectedChainRoot, setSelectedChainRoot] = useState<number | null>(null);
    const [selectedNodeForTree, setSelectedNodeForTree] = useState<number | null>(null);
    const [selectedLetterInModal, setSelectedLetterInModal] = useState<Letter | null>(null);

    const [highlightedNodeId, setHighlightedNodeId] = useState<number | null>(null);
    const [highlightedNodeInModal, setHighlightedNodeInModal] = useState<number | null>(null);

    const [dateFrom, setDateFrom] = useState<string>(getDefaultDateFrom());
    const [dateTo, setDateTo] = useState<string>("");
    const [numberQuery, setNumberQuery] = useState<string>("");
    const [createdBy, setCreatedBy] = useState<string>("");
    const [responsible, setResponsible] = useState<string>("");
    const [subject, setSubject] = useState<string>("");
    const [toPerson, setToPerson] = useState<string>("");
    const [fromPerson, setFromPerson] = useState<string>("");
    const [emailSender, setEmailSender] = useState<string>("");
    const [emailReceiver, setEmailReceiver] = useState<string>("");
    const [localFilterOpen, setLocalFilterOpen] = useState<boolean>(false);

    const isFiltersOpen = typeof isFilterOpen === 'boolean' ? isFilterOpen : localFilterOpen;
    // Черновые значения фильтров: редактируются в модалке, применяются по кнопке
    const [draftDateFrom, setDraftDateFrom] = useState<string>('');
    const [draftDateTo, setDraftDateTo] = useState<string>('');
    const [draftNumberQuery, setDraftNumberQuery] = useState<string>('');
    const [draftCreatedBy, setDraftCreatedBy] = useState<string>('');
    const [draftResponsible, setDraftResponsible] = useState<string>('');
    const [draftSubject, setDraftSubject] = useState<string>('');
    const [draftToPerson, setDraftToPerson] = useState<string>('');
    const [draftFromPerson, setDraftFromPerson] = useState<string>('');
    const [draftEmailSender, setDraftEmailSender] = useState<string>('');
    const [draftEmailReceiver, setDraftEmailReceiver] = useState<string>('');

    const openFilters = () => {
        setDraftDateFrom(dateFrom);
        setDraftDateTo(dateTo);
        setDraftNumberQuery(numberQuery);
        setDraftCreatedBy(createdBy);
        setDraftResponsible(responsible);
        setDraftSubject(subject);
        setDraftToPerson(toPerson);
        setDraftFromPerson(fromPerson);
        setDraftEmailSender(emailSender);
        setDraftEmailReceiver(emailReceiver);
        (setIsFilterOpen ? setIsFilterOpen(true) : setLocalFilterOpen(true));
    };
    const closeFilters = () => (setIsFilterOpen ? setIsFilterOpen(false) : setLocalFilterOpen(false));

    const FILTER_STORAGE_KEY = 'letters_graph_filters';

    const networkContainerRef = useRef<HTMLDivElement | null>(null);
    const networkInstanceRef = useRef<any>(null);
    const badgeCountsRef = useRef<Map<number, number>>(new Map());
    const isDrawingBadgesRef = useRef<boolean>(false);

    useEffect(() => {
        try {
            const stored = localStorage.getItem(FILTER_STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored) || {};
                setDateFrom(parsed.dateFrom || "");
                setDateTo(parsed.dateTo || "");
                setNumberQuery(parsed.numberQuery || "");
                setCreatedBy(parsed.createdBy || "");
                setResponsible(parsed.responsible || "");
                setSubject(parsed.subject || "");
                setToPerson(parsed.toPerson || "");
                setFromPerson(parsed.fromPerson || "");
                setEmailSender(parsed.emailSender || "");
                setEmailReceiver(parsed.emailReceiver || "");
            }
        } catch { }
    }, []);


    const findLetterByFilters = useCallback((letters: Letter[]): Letter | null => {
        const hasActiveFilters = !!(numberQuery || createdBy || responsible || subject || toPerson || fromPerson || emailSender || emailReceiver || dateFrom || dateTo);
        if (!hasActiveFilters) {
            return null;
        }

        const norm = (s: string) => (s || '').toLowerCase();

        const foundLetter = letters.find(letter => {
            if (numberQuery && !norm(letter.number).includes(norm(numberQuery))) return false;
            if (createdBy && !norm(letter.created_by).includes(norm(createdBy))) return false;
            if (responsible && !norm(letter.responsible_employee).includes(norm(responsible))) return false;
            if (subject && !norm(letter.subject).includes(norm(subject))) return false;
            if (toPerson && !norm(letter.to_person).includes(norm(toPerson))) return false;
            if (fromPerson && !norm(letter.from_person).includes(norm(fromPerson))) return false;
            if (emailSender && !norm(letter.email_sender).includes(norm(emailSender))) return false;
            if (emailReceiver && !norm(letter.email_receiver).includes(norm(emailReceiver))) return false;

            if (dateFrom || dateTo) {
                if (!letter.date) return false;
                const t = new Date(letter.date).getTime();
                if (dateFrom) {
                    const f = new Date(dateFrom).getTime();
                    if (t < f) return false;
                }
                if (dateTo) {
                    const tt = new Date(dateTo).getTime();
                    if (t > tt) return false;
                }
            }

            return true;
        });

        return foundLetter || null;
    }, [numberQuery, createdBy, responsible, subject, toPerson, fromPerson, emailSender, emailReceiver, dateFrom, dateTo]);


    useEffect(() => {
        try {
            localStorage.setItem(
                FILTER_STORAGE_KEY,
                JSON.stringify({ dateFrom, dateTo, numberQuery, createdBy, responsible, subject, toPerson, fromPerson, emailSender, emailReceiver })
            );
        } catch { }

        // логика открытия модалки при фильтра(дочерние,основные и т д)
        const foundLetter = findLetterByFilters(allLetters);
        if (foundLetter) {
            const isNumberFilter = !!numberQuery;

            const hasAnyConnections = allLinks.some(e => e.from === foundLetter.id || e.to === foundLetter.id);

            if (!hasAnyConnections) {
                if (isNumberFilter) {
                    setSelectedLetterInModal(foundLetter);
                } else {
                    setSelectedLetterInModal(null);
                }
                setHighlightedNodeId(foundLetter.id);
                setHighlightedNodeInModal(null);
                setSelectedNodeForTree(null);
            } else {
                const rootNodes = getRootNodes(allLetters, allLinks);
                const rootNodesSet = new Set(rootNodes);
                const isRootNode = rootNodesSet.has(foundLetter.id);

                if (isRootNode) {
                    const rootHasChildren = allLinks.some(e => e.from === foundLetter.id);

                    if (!rootHasChildren) {
                        if (isNumberFilter) {
                            setSelectedLetterInModal(foundLetter);
                        } else {
                            setSelectedLetterInModal(null);
                        }
                        setHighlightedNodeId(foundLetter.id);
                        setHighlightedNodeInModal(null);
                        setSelectedNodeForTree(null);
                    } else {
                        setHighlightedNodeId(foundLetter.id);
                        if (isNumberFilter) {
                            setSelectedNodeForTree(foundLetter.id);
                            setHighlightedNodeInModal(foundLetter.id);
                        } else {
                            setSelectedNodeForTree(null);
                            setHighlightedNodeInModal(null);
                        }
                    }
                } else {
                    const visited = new Set<number>();
                    let current: number | null = foundLetter.id;
                    let rootNodeId: number | null = null;

                    while (current !== null) {
                        if (visited.has(current)) break;
                        visited.add(current);

                        if (rootNodesSet.has(current)) {
                            rootNodeId = current;
                            break;
                        }

                        const parentEdge = allLinks.find(e => e.to === current);
                        current = parentEdge ? parentEdge.from : null;
                    }

                    const hasChildren = allLinks.some(e => e.from === foundLetter.id);

                    if (!hasChildren) {
                        if (isNumberFilter) {
                            setSelectedLetterInModal(foundLetter);
                        } else {
                            setSelectedLetterInModal(null);
                        }
                        setHighlightedNodeId(rootNodeId);
                        setHighlightedNodeInModal(null);
                        setSelectedNodeForTree(null);
                    } else {

                        setHighlightedNodeId(rootNodeId);
                        if (isNumberFilter) {
                            setSelectedNodeForTree(foundLetter.id);
                            setHighlightedNodeInModal(foundLetter.id);
                        } else {
                            setSelectedNodeForTree(null);
                            setHighlightedNodeInModal(foundLetter.id);
                        }
                    }
                }
            }
        } else {
            setHighlightedNodeId(null);
            setHighlightedNodeInModal(null);
            setSelectedLetterInModal(null);
            setSelectedNodeForTree(null);
        }
    }, [dateFrom, dateTo, numberQuery, createdBy, responsible, subject, toPerson, fromPerson, emailSender, emailReceiver, allLetters, allLinks, findLetterByFilters]);


    const handleResetFilters = () => {
        // Сброс в модалке — очищаем только черновики
        setDraftDateFrom('');
        setDraftDateTo('');
        setDraftNumberQuery('');
        setDraftCreatedBy('');
        setDraftResponsible('');
        setDraftSubject('');
        setDraftToPerson('');
        setDraftFromPerson('');
        setDraftEmailSender('');
        setDraftEmailReceiver('');
    };

    const handleApplyFilters = () => {
        setDateFrom(draftDateFrom || '');
        setDateTo(draftDateTo || '');
        setNumberQuery(draftNumberQuery || '');
        setCreatedBy(draftCreatedBy || '');
        setResponsible(draftResponsible || '');
        setSubject(draftSubject || '');
        setToPerson(draftToPerson || '');
        setFromPerson(draftFromPerson || '');
        setEmailSender(draftEmailSender || '');
        setEmailReceiver(draftEmailReceiver || '');
        setHighlightedNodeId(null);
        setHighlightedNodeInModal(null);
        closeFilters();
    };




    const handleExportChainToExcel = useCallback((chainRootId: number) => {
        const chain = getChainNodes(chainRootId, allLetters, allLinks);

        const exportData = chain.map((letter, index) => ({
            '№': index + 1,
            'Номер письма': letter.number,
            'Тип': letter.letter_type,
            'Дата': new Date(letter.date).toLocaleDateString('ru-RU'),
            'Организация': letter.organization || '',
            'Тема': letter.subject || '',
            'Кому': letter.to_person || '',
            'От кого': letter.from_person || '',
            'Ответственный': letter.responsible_employee || '',
            'Автор записи': letter.created_by || '',
            'Примечание': letter.note || '',
            'Внешняя информация': letter.external_letter_info || '',
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Цепочка писем');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const rootLetter = allLetters.find(l => l.id === chainRootId);
        const fileName = `Цепочка_${rootLetter?.number || chainRootId}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        saveAs(dataBlob, fileName);
    }, [allLetters, allLinks]);


    const handleExportChainToPdf = useCallback(async (chainRootId: number) => {
        if (!networkContainerRef.current || !networkInstanceRef.current) return;

        try {
            const chain = getChainNodes(chainRootId, allLetters, allLinks);
            const chainIds = new Set(chain.map(l => l.id));

            networkInstanceRef.current.setSelection({ nodes: Array.from(chainIds), edges: [] });

            await new Promise(resolve => setTimeout(resolve, 500));

            networkInstanceRef.current.fit({
                nodes: Array.from(chainIds),
                animation: { duration: 500 }
            });

            await new Promise(resolve => setTimeout(resolve, 1000));

            const canvas = await html2canvas(networkContainerRef.current, {
                backgroundColor: '#ffffff',
                scale: 2,
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('landscape', 'mm', 'a4');
            const imgWidth = 297;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

            const rootLetter = allLetters.find(l => l.id === chainRootId);
            const fileName = `Цепочка_${rootLetter?.number || chainRootId}_${new Date().toISOString().slice(0, 10)}.pdf`;
            pdf.save(fileName);

            networkInstanceRef.current.setSelection({ nodes: [], edges: [] });
        } catch (error) {
            console.error('Ошибка экспорта в PDF:', error);
            alert('Ошибка при экспорте в PDF');
        }
    }, [allLetters, allLinks]);




    const drawBadges = useCallback((ctx: CanvasRenderingContext2D) => {
        const network = networkInstanceRef.current;
        if (!network || !badgeCountsRef.current.size) return;
        
        if (isDrawingBadgesRef.current) return;
        isDrawingBadgesRef.current = true;

        // рзамер кружков
        const radius = 26;
        const lineWidth = 5;
        const fontSize = 24;
        const yOffset = radius * 0.5;

        badgeCountsRef.current.forEach((count, nodeId) => {
            if (count <= 0) return;

            const node = network.body?.nodes?.[nodeId];
            const boundingBox = node?.shape?.boundingBox;
            if (!boundingBox) return;

            // цвет кружка(от блока)
            const nodeOptions = (node as any)?.options || {};
            const blockColor = nodeOptions.color?.background || '#f1f5f9';
            const borderColor = '#6b7280';

            const centerX = (boundingBox.left + boundingBox.right) / 2;
            const centerY = boundingBox.bottom + yOffset;

            ctx.save();
            ctx.beginPath();
            ctx.fillStyle = blockColor;
            ctx.strokeStyle = borderColor;
            ctx.lineWidth = lineWidth;
            ctx.shadowColor = 'rgba(15,23,42,0.2)';
            ctx.shadowBlur = 3;
            ctx.shadowOffsetY = 1;
            ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();

            ctx.shadowColor = 'transparent';
            ctx.fillStyle = '#111827';
            ctx.font = `bold ${fontSize}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(String(count), centerX, centerY);
            ctx.restore();
        });
        
        requestAnimationFrame(() => {
            isDrawingBadgesRef.current = false;
        });
    }, []);


    const buildGraph = useCallback((data: ApiResponse) => {
        if (!networkContainerRef.current || !window.vis) return;

        let nodesRaw = filterType === 'all' ? data.nodes : data.nodes.filter(n => n.letter_type === filterType);

        const norm = (s: string) => (s || '').toLowerCase();
        const inRange = (d: string) => {
            if (!d) return false;
            const t = new Date(d).getTime();
            if (dateFrom) {
                const f = new Date(dateFrom).getTime();
                if (t < f) return false;
            }
            if (dateTo) {
                const tt = new Date(dateTo).getTime();
                if (t > tt) return false;
            }
            return true;
        };

        nodesRaw = nodesRaw.filter(n => {
            if (dateFrom || dateTo) if (!inRange(n.date)) return false;
            if (numberQuery && !norm(n.number).includes(norm(numberQuery))) return false;
            if (createdBy && !norm(n.created_by).includes(norm(createdBy))) return false;
            if (responsible && !norm(n.responsible_employee).includes(norm(responsible))) return false;
            if (subject && !norm(n.subject).includes(norm(subject))) return false;
            if (toPerson && !norm(n.to_person).includes(norm(toPerson))) return false;
            if (fromPerson && !norm(n.from_person).includes(norm(fromPerson))) return false;
            if (emailSender && !norm(n.email_sender).includes(norm(emailSender))) return false;
            if (emailReceiver && !norm(n.email_receiver).includes(norm(emailReceiver))) return false;
            if (globalSearch) {
                const q = norm(globalSearch);
                const matched = [
                    n.number, n.subject, n.from_person, n.to_person, n.organization,
                    n.to_organization, n.responsible_employee, n.created_by, n.note,
                    n.email_sender, n.email_receiver, n.external_letter_info, n.external_executor,
                ].some(field => norm(field).includes(q));
                if (!matched) return false;
            }
            return true;
        });

        const uniq: Record<string, Link> = {};
        (data.edges || []).forEach(e => {
            const k = `${e.from}-${e.to}`;
            const r = `${e.to}-${e.from}`;
            if (!uniq[k] && !uniq[r]) uniq[k] = e;
        });
        let edgesRaw = Object.values(uniq);

        const presentIds = new Set(nodesRaw.map(n => n.id));
        edgesRaw = edgesRaw.filter(e => presentIds.has(e.from) && presentIds.has(e.to));

        if (connectedOnly) {
            const connectedIds = new Set<number>();
            edgesRaw.forEach(e => { connectedIds.add(e.from); connectedIds.add(e.to); });
            nodesRaw = nodesRaw.filter(n => connectedIds.has(n.id));
            const p2 = new Set(nodesRaw.map(n => n.id));
            edgesRaw = edgesRaw.filter(e => p2.has(e.from) && p2.has(e.to));
        }

        // поиск писем, которые соответствуют фильтрам (включая дочерние)
        const foundLetters = nodesRaw.filter(n => {
            const hasAnyFilter = numberQuery || createdBy || responsible || subject || toPerson || fromPerson || emailSender || emailReceiver || dateFrom || dateTo;
            if (!hasAnyFilter) return false;

            const norm = (s: string) => (s || '').toLowerCase();
            if (numberQuery && !norm(n.number).includes(norm(numberQuery))) return false;
            if (createdBy && !norm(n.created_by).includes(norm(createdBy))) return false;
            if (responsible && !norm(n.responsible_employee).includes(norm(responsible))) return false;
            if (subject && !norm(n.subject).includes(norm(subject))) return false;
            if (toPerson && !norm(n.to_person).includes(norm(toPerson))) return false;
            if (fromPerson && !norm(n.from_person).includes(norm(fromPerson))) return false;
            if (emailSender && !norm(n.email_sender).includes(norm(emailSender))) return false;
            if (emailReceiver && !norm(n.email_receiver).includes(norm(emailReceiver))) return false;
            if (dateFrom || dateTo) {
                if (!n.date) return false;
                const t = new Date(n.date).getTime();
                if (dateFrom) {
                    const f = new Date(dateFrom).getTime();
                    if (t < f) return false;
                }
                if (dateTo) {
                    const tt = new Date(dateTo).getTime();
                    if (t > tt) return false;
                }
            }
            return true;
        });

        // поиск родительских корневых узлов для найденных дочерних писем
        const rootNodes = getRootNodes(nodesRaw, edgesRaw);
        const rootNodesSet = new Set(rootNodes);
        const nodesToShow = new Set<number>(rootNodes);

        // для каждого найденного письма находим его корневой узел
        foundLetters.forEach(foundLetter => {
            if (rootNodesSet.has(foundLetter.id)) {

                nodesToShow.add(foundLetter.id);
            } else {
                const visited = new Set<number>();
                let current: number | null = foundLetter.id;

                while (current !== null) {
                    if (visited.has(current)) break;
                    visited.add(current);

                    if (rootNodesSet.has(current)) {
                        nodesToShow.add(current);
                        break;
                    }

                    const parentEdge = edgesRaw.find(e => e.to === current);
                    current = parentEdge ? parentEdge.from : null;
                }
            }
        });

        const visibleNodesRaw = nodesRaw.filter(n => nodesToShow.has(n.id));

        const badgeCounts = new Map<number, number>();

        const nodes = new window.vis.DataSet(visibleNodesRaw.map(n => {
            const isRoot = rootNodesSet.has(n.id);
            
            // количетва связей
            const chain = getChainNodes(n.id, allLetters, allLinks);
            const connectionsCount = chain.length > 0 ? chain.length - 1 : 0;
            
            const isHighlighted = highlightedNodeId === n.id;
            
            const numberText = isRoot ? `+ ${n.number}` : n.number;
            const label = numberText;

            badgeCounts.set(n.id, connectionsCount);

            return {
                id: n.id,
                label: label,
                letter_type: n.letter_type,
                title: `${n.number}\n${new Date(n.date).toLocaleDateString('ru-RU')}`,
                color: {
                    border: isHighlighted ? '#FF6B6B' : '#666',
                    background: getColor(n.letter_type),
                    highlight: { border: '#FF6B6B', background: getColor(n.letter_type) },
                    hover: { border: '#2B7CE9', background: getColor(n.letter_type) }
                },
                shape: 'box',
                font: { size: 28, color: '#333', face: 'Arial', bold: isRoot || isHighlighted },
                margin: 26,
                borderWidth: isHighlighted ? 4 : 2,
                borderWidthSelected: isHighlighted ? 5 : 3,
                widthConstraint: { maximum: 520 },
                heightConstraint: { maximum: 140 },
            };
        }));

        badgeCountsRef.current = badgeCounts;
        const edges = new window.vis.DataSet([]);

        const options = {
            interaction: { hover: true, tooltipDelay: 100, navigationButtons: true, keyboard: true, zoomSpeed: 0.3 },
            physics: {
                enabled: true,
                solver: 'forceAtlas2Based',
                forceAtlas2Based: {
                    gravitationalConstant: -200,
                    centralGravity: 0.005,
                    springLength: 60,
                    springConstant: 0.015,
                    damping: 0.4,
                    avoidOverlap: 1
                },
                stabilization: { iterations: 250, updateInterval: 10 }
            },
            edges: { smooth: false },
            nodes: { scaling: { min: 36, max: 54 } },
            layout: { improvedLayout: false }
        } as const;

        if (networkInstanceRef.current) {
            networkInstanceRef.current.setData({ nodes, edges });
            networkInstanceRef.current.setOptions(options);
        } else {
            networkInstanceRef.current = new window.vis.Network(networkContainerRef.current, { nodes, edges }, options);
        }

        setGraphReady(true);

        if (networkInstanceRef.current) {
            networkInstanceRef.current.off('afterDrawing', drawBadges);
            networkInstanceRef.current.on('afterDrawing', drawBadges);
        }

        setCounts({ nodes: nodes.length, edges: edges.length });

        if (networkInstanceRef.current) {
            networkInstanceRef.current.off('click');
            networkInstanceRef.current.on('click', (params: any) => {
                if (params.nodes.length > 0) {
                    const nodeId = params.nodes[0];
                    const letter = nodesRaw.find(n => n.id === nodeId);

                    if (rootNodesSet.has(nodeId)) {
                        const hasChildren = allLinks.some(e => e.from === nodeId);

                        if (!hasChildren) {
                            if (letter) {
                                setSelectedLetterInModal(letter);
                            }
                            setSelectedNodeForTree(null);
                        } else {

                            if (highlightedNodeInModal !== null) {
                                setSelectedNodeForTree(nodeId);
                            } else {
                                setSelectedNodeForTree(nodeId);
                                setHighlightedNodeInModal(null);
                            }
                        }
                    } else if (letter) {
                        setSelectedLetterInModal(letter);
                        setSelectedNodeForTree(null);
                    }
                } else {
                    setSelectedNodeForTree(null);
                }
            });
        }
    }, [filterType, connectedOnly, globalSearch, dateFrom, dateTo, numberQuery, createdBy, responsible, subject, toPerson, fromPerson, emailSender, emailReceiver, highlightedNodeId, highlightedNodeInModal, allLinks, allLetters, drawBadges]);



    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && selectedNodeForTree !== null) {
                setSelectedNodeForTree(null);
                setSelectedLetterInModal(null);
                setHighlightedNodeInModal(null);
            }
        };

        if (selectedNodeForTree !== null) {
            window.addEventListener('keydown', handleKeyDown);
            return () => {
                window.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [selectedNodeForTree]);



    useEffect(() => {
        const fetchData = async () => {
            try {
                // проверка кеша(чтобы не грузить данные каждый раз)
                const CACHE_KEY = 'letters_graph_cache';
                const CACHE_TIMESTAMP_KEY = 'letters_graph_cache_timestamp';
                const CACHE_DURATION = 5 * 60 * 1000;

                const cachedData = localStorage.getItem(CACHE_KEY);
                const cacheTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
                const now = Date.now();

                // если есть кеш и он не устарел, то используется старый
                if (cachedData && cacheTimestamp) {
                    const cacheAge = now - parseInt(cacheTimestamp, 10);
                    if (cacheAge < CACHE_DURATION) {
                        try {
                            const parsedData = JSON.parse(cachedData) as ApiResponse;
                            setMetaMs(parsedData.execution_time_ms);
                            setGraphReady(false);
                            setAllLetters(parsedData.nodes || []);
                            setAllLinks(parsedData.edges || []);
                            setLoading(false);
                            return;
                        } catch (e) {
                            console.warn('Ошибка парсинга кеша, загружаем заново:', e);
                        }
                    }
                }

                setGraphReady(false);
                setLoading(true);
                setError(null);
                const access = localStorage.getItem('access');
                const resp = await axios.get<ApiResponse>(getApiV1Url('LETTERS_GRAPH'), {
                    headers: access ? { Authorization: `Bearer ${access}` } : undefined,
                });

                try {
                    localStorage.setItem(CACHE_KEY, JSON.stringify(resp.data));
                    localStorage.setItem(CACHE_TIMESTAMP_KEY, now.toString());
                } catch (e) {
                    console.warn('Не удалось сохранить кеш:', e);
                }

                setMetaMs(resp.data.execution_time_ms);
                setAllLetters(resp.data.nodes || []);
                setAllLinks(resp.data.edges || []);
            } catch (e) {
                console.error('Ошибка загрузки графа писем:', e);
                setError('Ошибка загрузки данных.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);


    useEffect(() => {
        if ((window as any).vis) return;
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/vis-network@9.1.2/dist/vis-network.min.js';
        script.async = true;
        script.onload = () => {
            if (allLetters.length || allLinks.length) buildGraph({ nodes: allLetters, edges: allLinks });
        };
        document.body.appendChild(script);
        return () => { };
    }, [allLetters.length, allLinks.length, buildGraph]);


    useEffect(() => {
        if (!allLetters.length && !allLinks.length) return;
        if (!(window as any).vis) return;
        buildGraph({ nodes: allLetters, edges: allLinks });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterType, connectedOnly, globalSearch, dateFrom, dateTo, numberQuery, createdBy, responsible, subject, toPerson, fromPerson, emailSender, emailReceiver, allLetters, allLinks, highlightedNodeId]);




    const createdByOptions = useMemo(() =>
        Array.from(new Set(allLetters.map(l => (l.created_by || '').trim()).filter(Boolean))).sort(),
        [allLetters]
    );
    const responsibleOptions = useMemo(() =>
        Array.from(new Set(allLetters.map(l => (l.responsible_employee || '').trim()).filter(Boolean))).sort(),
        [allLetters]
    );
    const toPersonOptions = useMemo(() =>
        Array.from(new Set(allLetters.map(l => (l.to_person || '').replace(/\r?\n/g, ' ').trim()).filter(Boolean))).sort(),
        [allLetters]
    );
    const subjectOptions = useMemo(() =>
        Array.from(new Set(allLetters.map(l => (l.subject || '').replace(/\r?\n/g, ' ').trim()).filter(Boolean))).sort(),
        [allLetters]
    );
    const fromPersonOptions = useMemo(() =>
        Array.from(new Set(allLetters.map(l => (l.from_person || '').replace(/\r?\n/g, ' ').trim()).filter(Boolean))).sort(),
        [allLetters]
    );
    const emailSenderOptions = useMemo(() =>
        Array.from(new Set(allLetters.map(l => (l.email_sender || '').trim()).filter(Boolean))).sort(),
        [allLetters]
    );
    const emailReceiverOptions = useMemo(() =>
        Array.from(new Set(allLetters.map(l => (l.email_receiver || '').trim()).filter(Boolean))).sort(),
        [allLetters]
    );

    const activeEntries = useMemo(() => {
        const entries: { key: string; title: string; value: string }[] = [];
        if (dateFrom || dateTo) entries.push({ key: 'period', title: 'Период', value: `${dateFrom || '...'} — ${dateTo || '...'}` });
        if (numberQuery) entries.push({ key: 'number', title: 'Номер', value: numberQuery });
        if (createdBy) entries.push({ key: 'created_by', title: 'Автор', value: createdBy });
        if (responsible) entries.push({ key: 'responsible', title: 'Ответственный', value: responsible });
        if (toPerson) entries.push({ key: 'to_person', title: 'Кому', value: toPerson });
        if (subject) entries.push({ key: 'subject', title: 'Тема', value: subject });
        if (fromPerson) entries.push({ key: 'from_person', title: 'От кого', value: fromPerson });
        if (emailSender) entries.push({ key: 'email_sender', title: 'Email отправителя', value: emailSender });
        if (emailReceiver) entries.push({ key: 'email_receiver', title: 'Email получателя', value: emailReceiver });
        if (connectedOnly) entries.push({ key: 'connected', title: 'Фильтр', value: 'Только со связями' });
        return entries;
    }, [dateFrom, dateTo, numberQuery, createdBy, responsible, toPerson, subject, fromPerson, emailSender, emailReceiver, connectedOnly]);


    const handleRemoveActive = (key: string) => {
        switch (key) {
            case 'period': setDateFrom(''); setDateTo(''); break;
            case 'number': setNumberQuery(''); break;
            case 'created_by': setCreatedBy(''); break;
            case 'responsible': setResponsible(''); break;
            case 'to_person': setToPerson(''); break;
            case 'subject': setSubject(''); break;
            case 'from_person': setFromPerson(''); break;
            case 'email_sender': setEmailSender(''); break;
            case 'email_receiver': setEmailReceiver(''); break;
            case 'connected': setConnectedOnly(false); break;
            default: break;
        }
    };


    return (
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
                <button
                    onClick={() => navigate('/')}
                    style={{
                        padding: '8px 16px',
                        border: '1px solid #d1d5db',
                        background: '#0074b7',
                        color: '#fff',
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 14,
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                    }}
                    aria-label="Вернуться на главную"
                >
                    ← Назад
                </button>
                <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>Граф писем</h1>
            </div>

            <div style={{ padding: '16px 20px', flexShrink: 0 }}>
                <GraphControls
                    filterType={filterType}
                    onFilterTypeChange={setFilterType}
                    connectedOnly={connectedOnly}
                    onConnectedOnlyChange={setConnectedOnly}
                    onOpenFilters={openFilters}
                    activeFiltersCount={activeEntries.length}
                    metaMs={metaMs}
                    counts={counts}
                    globalSearch={globalSearch}
                    onGlobalSearchChange={setGlobalSearch}
                />
            </div>

            <div style={{ display: 'flex', gap: 0, flex: 1, minHeight: 0, padding: '0 20px 20px 20px' }}>
                <div style={{
                    flex: selectedLetterInModal && !selectedNodeForTree ? 1 : 1,
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: selectedLetterInModal && !selectedNodeForTree ? '8px 0 0 8px' : '8px',
                    position: 'relative',
                    transition: 'flex 0.3s ease'
                }}>
                    <div ref={networkContainerRef} style={{ height: '100%', width: '100%' }} />
                </div>

                {selectedLetterInModal && !selectedNodeForTree && (
                    <LetterInfoPanel
                        letter={selectedLetterInModal}
                        onClose={() => setSelectedLetterInModal(null)}
                    />
                )}
            </div>

            {(!graphReady || loading) && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(255, 255, 255, 0.92)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2000,
                    gap: 16
                }}>
                    <img
                        src="/loading.png"
                        alt="Загрузка..."
                        style={{ width: 200, height: 200, animation: 'spin 1.2s linear infinite' }}
                    />
                    <div style={{ fontSize: 18, color: '#374151', fontWeight: 600 }}>
                        Загрузка графа...
                    </div>
                    <style>{`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}</style>
                </div>
            )}
            {error && (<div style={{ marginTop: 12, color: '#b91c1c' }}>{error}</div>)}

            {isFiltersOpen && (
                <div className="certificates-filter-modal-overlay" onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        closeFilters();
                    }
                }}>
                    <LettersFilter
                        dateFrom={draftDateFrom}
                        dateTo={draftDateTo}
                        numberQuery={draftNumberQuery}
                        createdBy={draftCreatedBy}
                        responsible={draftResponsible}
                        subject={draftSubject}
                        toPerson={draftToPerson}
                        fromPerson={draftFromPerson}
                        emailSender={draftEmailSender}
                        emailReceiver={draftEmailReceiver}
                        activeCount={activeEntries.length}
                        activeEntries={activeEntries}
                        onRemoveActive={handleRemoveActive}
                        onChange={(next: Partial<LettersFilterValues>) => {
                            if ('dateFrom' in next) setDraftDateFrom(next.dateFrom || '');
                            if ('dateTo' in next) setDraftDateTo(next.dateTo || '');
                            if ('numberQuery' in next) setDraftNumberQuery(next.numberQuery || '');
                            if ('createdBy' in next) setDraftCreatedBy(next.createdBy || '');
                            if ('responsible' in next) setDraftResponsible(next.responsible || '');
                            if ('subject' in next) setDraftSubject(next.subject || '');
                            if ('toPerson' in next) setDraftToPerson(next.toPerson || '');
                            if ('fromPerson' in next) setDraftFromPerson(next.fromPerson || '');
                            if ('emailSender' in next) setDraftEmailSender(next.emailSender || '');
                            if ('emailReceiver' in next) setDraftEmailReceiver(next.emailReceiver || '');
                        }}
                        onReset={handleResetFilters}
                        onApply={handleApplyFilters}
                        onClose={closeFilters}
                        createdByOptions={createdByOptions}
                        responsibleOptions={responsibleOptions}
                        toPersonOptions={toPersonOptions}
                        subjectOptions={subjectOptions}
                        fromPersonOptions={fromPersonOptions}
                        emailSenderOptions={emailSenderOptions}
                        emailReceiverOptions={emailReceiverOptions}
                    />
                </div>
            )}

            {selectedNodeForTree !== null && (() => {
                const selectedLetter = allLetters.find(l => l.id === selectedNodeForTree);
                if (!selectedLetter) return null;

                return (
                    <TreeModal
                        selectedNodeId={selectedNodeForTree}
                        selectedLetter={selectedLetter}
                        allLetters={allLetters}
                        allLinks={allLinks}
                        highlightedNodeInModal={highlightedNodeInModal}
                        selectedLetterInModal={selectedLetterInModal}
                        onClose={() => {
                            setSelectedNodeForTree(null);
                            setSelectedLetterInModal(null);
                            setHighlightedNodeInModal(null);
                        }}
                        onLetterSelect={(letter) => setSelectedLetterInModal(letter)}
                        onLetterInfoClose={() => setSelectedLetterInModal(null)}
                        onExportToExcel={handleExportChainToExcel}
                    />
                );
            })()}

            {selectedChainRoot !== null && (
                <div
                    className="certificates-filter-modal-overlay"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setSelectedChainRoot(null);
                        }
                    }}
                    style={{ zIndex: 1001 }}
                >
                    <div
                        className="certificates-filter-modal"
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: 500 }}
                    >
                        <button
                            className="certificates-filter-modal__close"
                            onClick={() => setSelectedChainRoot(null)}
                            aria-label="Закрыть"
                        >
                            ×
                        </button>
                        <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 600 }}>
                            Экспорт цепочки писем
                        </h3>
                        <div style={{ marginBottom: 16 }}>
                            <p style={{ margin: '0 0 8px 0', fontSize: 14, color: '#374151' }}>
                                Корневое письмо: {allLetters.find(l => l.id === selectedChainRoot)?.number || selectedChainRoot}
                            </p>
                            <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
                                Писем в цепочке: {getChainNodes(selectedChainRoot, allLetters, allLinks).length}
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: 12, flexDirection: 'column' }}>
                            <button
                                onClick={() => {
                                    handleExportChainToExcel(selectedChainRoot);
                                    setSelectedChainRoot(null);
                                }}
                                style={{
                                    padding: '10px 16px',
                                    border: 'none',
                                    background: '#10b981',
                                    color: '#fff',
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    fontWeight: 500
                                }}
                                aria-label="Экспортировать в Excel"
                            >
                                Экспортировать в Excel
                            </button>
                            <button
                                onClick={async () => {
                                    await handleExportChainToPdf(selectedChainRoot);
                                    setSelectedChainRoot(null);
                                }}
                                style={{
                                    padding: '10px 16px',
                                    border: 'none',
                                    background: '#3b82f6',
                                    color: '#fff',
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    fontWeight: 500
                                }}
                                aria-label="Экспортировать в PDF"
                            >
                                Экспортировать в PDF
                            </button>
                            <button
                                onClick={() => setSelectedChainRoot(null)}
                                style={{
                                    padding: '10px 16px',
                                    border: '1px solid #d1d5db',
                                    background: '#fff',
                                    color: '#374151',
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                    fontSize: 14
                                }}
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LetterGraphFlowPage;
